import { TrendingUp, Clock, Award, Shield, AlertCircle, DollarSign, Percent, Zap, Rocket, Users, UserCheck, Headphones, LifeBuoy, CheckCircle, Timer, Lock } from 'lucide-react';

// Icon mapping
const iconMap: { [key: string]: any } = {
  TrendingUp,
  Clock,
  Award,
  Shield,
  AlertCircle,
  DollarSign,
  Percent,
  Zap,
  Rocket,
  Users,
  UserCheck,
  Headphones,
  LifeBuoy,
  CheckCircle,
  Timer,
  Lock,
};

interface BenefitCard {
  icon: string;
  gradientFrom: string;
  gradientTo: string;
  title: string;
  description: string;
  visible: boolean;
}

interface SmartInvestmentProps {
  badge: string;
  title: string;
  subtitle: string;
  benefits: BenefitCard[];
  visible: boolean;
}

export function SmartInvestmentSection({ badge, title, subtitle, benefits, visible }: SmartInvestmentProps) {
  if (!visible) return null;

  return (
    <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#2F2E5C] via-[#1a1935] to-[#2F2E5C] text-white overflow-hidden">
      {/* Premium Multi-Layer Background System */}
      <div className="absolute inset-0">
        {/* Vibrant Gradient Orbs */}
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-[#E87D3E] via-[#E8B84D] to-transparent opacity-20 rounded-full blur-3xl animate-pulse" style={{animationDuration: '4s'}} />
        <div className="absolute bottom-0 right-0 w-[700px] h-[700px] bg-gradient-to-tl from-[#D74444] via-[#e85e5e] to-transparent opacity-20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-r from-[#4a487a] to-transparent opacity-15 rounded-full blur-3xl" />
      </div>

      {/* Premium Radial Spotlight Effect */}
      <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-[#0a0a1f]/40" />

      {/* Matte Texture Overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />

      {/* Frosted Glass Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.08]" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.4) 1px, transparent 0)', backgroundSize: '50px 50px'}} />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          {/* Premium Frosted Glass Badge */}
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/30 shadow-lg">
            <Award className="w-4 h-4 text-[#E8B84D]" />
            <span className="text-sm font-semibold bg-gradient-to-r from-[#E8B84D] to-[#E87D3E] bg-clip-text text-transparent">{badge}</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-white via-white to-white/80 bg-clip-text text-transparent">
              {title}
            </span>
          </h2>
          <p className="text-base text-white/90 max-w-2xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        </div>
        
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.filter(b => b.visible).map((benefit, index) => {
            const IconComponent = iconMap[benefit.icon] || Award;
            
            return (
              <div key={index} className="group relative">
                {/* Glow Effect Behind Card */}
                <div 
                  className="absolute inset-0 rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity"
                  style={{
                    background: `linear-gradient(to bottom right, ${benefit.gradientFrom}, ${benefit.gradientTo})`
                  }}
                />
                
                {/* Card Container */}
                <div className="relative bg-white/10 backdrop-blur-xl rounded-3xl border border-white/20 p-6 shadow-2xl hover:bg-white/15 transition-all overflow-hidden h-full">
                  {/* Decorative Corner Gradient */}
                  <div 
                    className="absolute top-0 right-0 w-24 h-24 rounded-full blur-2xl opacity-20"
                    style={{
                      background: `linear-gradient(to bottom left, ${benefit.gradientFrom}, transparent)`
                    }}
                  />
                  
                  {/* Subtle Pattern */}
                  <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '30px 30px'}} />
                  
                  <div className="relative z-10">
                    {/* Icon with Glow */}
                    <div className="relative mb-4">
                      <div 
                        className="absolute inset-0 rounded-2xl blur-md opacity-50"
                        style={{
                          background: `linear-gradient(to bottom right, ${benefit.gradientFrom}, ${benefit.gradientTo})`
                        }}
                      />
                      <div 
                        className="relative w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform"
                        style={{
                          background: `linear-gradient(to bottom right, ${benefit.gradientFrom}, ${benefit.gradientTo})`
                        }}
                      >
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                    </div>
                    
                    <h3 className="font-bold mb-2">
                      <span className="bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent">
                        {benefit.title}
                      </span>
                    </h3>
                    <p className="text-sm text-white/80 leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}